import { useMemo, useState, type FormEvent } from 'react'
import {
  isSessionActive,
  listAllocations,
  listAttendanceSessions,
  openAttendanceSession,
} from '@/lib/api'
import { useAsyncData } from '@/lib/useAsyncData'

function todayISO() {
  return new Date().toISOString().slice(0, 10)
}

function timeNowPlus(minutes: number) {
  const d = new Date(Date.now() + minutes * 60_000)
  return d.toTimeString().slice(0, 8)
}

export function LecturerAttendancePage() {
  const allocations = useAsyncData(listAllocations, [])
  const sessions = useAsyncData(listAttendanceSessions, [])
  const [allocationId, setAllocationId] = useState('')
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState<string | null>(null)

  const options = useMemo(() => allocations.data || [], [allocations.data])

  async function onOpen(e: FormEvent) {
    e.preventDefault()
    if (!allocationId) return
    setBusy(true)
    setMessage(null)
    try {
      await openAttendanceSession({
        course_allocation: Number(allocationId),
        session_date: todayISO(),
        start_time: timeNowPlus(-1),
        end_time: timeNowPlus(60),
        qr_code_token: crypto.randomUUID().replace(/-/g, ''),
      })
      setMessage('Attendance session opened for 60 minutes.')
      await sessions.reload()
    } catch (err) {
      setMessage(err instanceof Error ? err.message : 'Failed to open session')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="space-y-6 p-8">
      <h1 className="text-2xl font-bold text-slate-800">Open Attendance</h1>
      {message && <p className="rounded-xl bg-slate-100 px-4 py-3 text-sm text-slate-700">{message}</p>}

      <form
        onSubmit={onOpen}
        className="flex flex-col gap-3 rounded-2xl border border-slate-100 bg-white p-5 shadow-sm md:flex-row md:items-end"
      >
        <label className="flex-1 text-xs font-semibold text-slate-600">
          Course
          <select
            className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
            value={allocationId}
            onChange={(e) => setAllocationId(e.target.value)}
            required
          >
            <option value="">Select allocation…</option>
            {options.map((a) => (
              <option key={a.id} value={a.id}>
                {a.course_details.code} — {a.course_details.title}
              </option>
            ))}
          </select>
        </label>
        <button
          type="submit"
          disabled={busy || !allocationId}
          className="rounded-xl bg-emerald-600 px-5 py-2.5 text-sm font-semibold text-white disabled:bg-slate-300"
        >
          {busy ? 'Opening…' : 'Open 60‑min session'}
        </button>
      </form>

      <div className="space-y-3">
        {(sessions.data || []).map((s) => (
          <div key={s.id} className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm">
            <p className="text-sm font-bold text-slate-800">
              {s.course_title} ({s.course_code})
            </p>
            <p className="text-xs text-slate-500">
              {s.session_date} · {s.start_time.slice(0, 5)}–{s.end_time.slice(0, 5)}
              {isSessionActive(s) ? ' · Active' : ''}
            </p>
            {s.qr_code_token && (
              <p className="mt-2 font-mono text-[11px] break-all text-slate-400">QR token: {s.qr_code_token}</p>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}
